import React, { useState, useEffect } from 'react';
import api, { setAuthToken } from '../services/api';
import { track } from '../services/analytics';

const ClientLogin = ({ onLoginSuccess, recoveryMode = false, onCancelRecovery, initialTab = 'login', resetToken = '' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // États pour la récupération de mot de passe
  const [viewState, setViewState] = useState((recoveryMode || resetToken) ? 'reset' : initialTab); // 'login' | 'register' | 'forgot' | 'reset'
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // États pour l'inscription en libre-service
  const [registerNom, setRegisterNom] = useState('');
  const [registerSiren, setRegisterSiren] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerRegime, setRegisterRegime] = useState('reel_normal');
  const [registerFrequence, setRegisterFrequence] = useState('mensuelle');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isCreatorMode, setIsCreatorMode] = useState(false); // Inscription sans SIREN (Créateur)

  useEffect(() => {
    if (recoveryMode) {
      setViewState('reset');
      setError('');
      setSuccessMessage('');
    }
  }, [recoveryMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authentification locale : bcrypt + JWT du backend (plus de Supabase)
      const login = await api.post('/auth/login', {
        email: email.trim(),
        password: password
      });

      const token = login?.data?.access_token;
      if (!login || !login.success || !token) {
        setError(login?.error || 'Identifiants incorrects');
        return;
      }

      setAuthToken(token);

      // Récupérer les infos locales du dossier client
      const response = await api.get('/auth/me');

      if (response && response.success && response.role === 'client' && response.client) {
        onLoginSuccess(response.client.id, response.client.nom);
      } else {
        setError(response?.error || 'Dossier client introuvable ou désactivé.');
      }
    } catch (err) {
      console.error('Erreur connexion:', err);
      setError(err.message || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validations
    if (!registerNom.trim()) {
      setError("Le nom de l'entreprise ou du projet est requis.");
      return;
    }
    if (!isCreatorMode && !/^[0-9]{9}$/.test(registerSiren.trim())) {
      setError("Le SIREN doit contenir exactement 9 chiffres.");
      return;
    }
    if (registerPassword.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (!/[A-Z]/.test(registerPassword) || !/[a-z]/.test(registerPassword) || !/[0-9]/.test(registerPassword)) {
      setError("Le mot de passe doit inclure une majuscule, une minuscule et un chiffre.");
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);

    try {
      let response;
      if (isCreatorMode) {
        // Inscription Créateur d'entreprise (Sans SIREN)
        response = await api.post('/auth/register-projet', {
          email: registerEmail.trim(),
          password: registerPassword,
          nom_projet: registerNom.trim()
        });
      } else {
        // Inscription entreprise déjà immatriculée (Avec SIREN)
        response = await api.post('/auth/register', {
          email: registerEmail.trim(),
          password: registerPassword,
          nom: registerNom.trim(),
          siren: registerSiren.trim(),
          regime_tva: registerRegime,
          frequence_tva: registerFrequence
        });
      }

      if (!response.success) {
        throw new Error(response.message || response.error || "Erreur lors de l'inscription");
      }

      setSuccessMessage("✅ Compte créé avec succès ! Connexion automatique...");
      track('signup', isCreatorMode ? 'signup_projet' : 'signup_client');

      // 2. Connexion automatique via l'API locale
      const login = await api.post('/auth/login', {
        email: registerEmail.trim(),
        password: registerPassword
      });

      const token = login?.data?.access_token;
      if (!login || !login.success || !token) {
        throw new Error("Compte créé, mais la connexion automatique a échoué. Connectez-vous manuellement.");
      }

      setAuthToken(token);

      // Appeler /auth/me
      const profile = await api.get('/auth/me');
      if (profile && profile.success && profile.role === 'client' && profile.client) {
        setTimeout(() => {
          onLoginSuccess(profile.client.id, profile.client.nom);
        }, 1500);
      } else {
        setError("Erreur lors de la récupération du profil.");
      }
    } catch (err) {
      console.error("Erreur inscription:", err);
      setError(err.message || "Impossible de créer le compte.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recoveryEmail.trim())) {
      setError("Format d'email invalide.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: recoveryEmail.trim() });
      // Réponse neutre : ne révèle jamais si le compte existe.
      setSuccessMessage(
        (res && res.success && res.message)
          ? res.message
          : "Si un compte existe avec cette adresse, un email de réinitialisation vient d'être envoyé."
      );
    } catch (err) {
      console.error('Erreur demande de réinitialisation:', err);
      setSuccessMessage("Si un compte existe avec cette adresse, un email de réinitialisation vient d'être envoyé.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!resetToken) {
      setError("Lien de réinitialisation manquant ou invalide. Refaites une demande.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError("Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', {
        token: resetToken,
        new_password: newPassword
      });

      if (!res || !res.success) {
        setError(res?.error || "Lien invalide ou expiré. Refaites une demande.");
        return;
      }

      setSuccessMessage("✅ Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.");
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        setViewState('login');
        setError('');
        setSuccessMessage('');
        setEmail(recoveryEmail || email);
        setPassword('');
      }, 2500);
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      setError(err.message || "Erreur lors de la mise à jour du mot de passe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F4ED', fontFamily: 'Inter, system-ui, sans-serif', padding: '20px'}}>
      <div style={{background: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: '440px'}}>
        
        {/* Logo */}
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <div style={{width: '60px', height: '60px', borderRadius: '12px', background: '#1D9E75', color: 'white', fontSize: '30px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'}}>
            C
          </div>
          <h1 style={{fontSize: '24px', fontWeight: '600', color: '#2D2D2D', marginBottom: '8px'}}>
            {viewState === 'login' && 'Connexion Client'}
            {viewState === 'register' && 'Création de Compte'}
            {viewState === 'forgot' && 'Mot de passe oublié'}
            {viewState === 'reset' && 'Nouveau mot de passe'}
          </h1>
          <p style={{fontSize: '14px', color: '#73726C'}}>
            {viewState === 'login' && 'Accédez à votre espace comptable'}
            {viewState === 'register' && 'Rejoignez ComptaAI en libre-service'}
            {viewState === 'forgot' && 'Saisissez votre email pour recevoir le lien de réinitialisation'}
            {viewState === 'reset' && 'Définissez votre nouveau mot de passe'}
          </p>
        </div>

        {/* Onglets Connexion / Inscription */}
        {(viewState === 'login' || viewState === 'register') && (
          <div style={{ display: 'flex', borderBottom: '1px solid #E8E7E3', marginBottom: '24px' }}>
            <button
              onClick={() => { setViewState('login'); setError(''); setSuccessMessage(''); }}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: '600',
                color: viewState === 'login' ? '#1D9E75' : '#73726C',
                borderBottom: viewState === 'login' ? '2px solid #1D9E75' : 'none',
                cursor: 'pointer'
              }}
            >
              Se connecter
            </button>
            <button
              onClick={() => { setViewState('register'); setError(''); setSuccessMessage(''); }}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: '600',
                color: viewState === 'register' ? '#1D9E75' : '#73726C',
                borderBottom: viewState === 'register' ? '2px solid #1D9E75' : 'none',
                cursor: 'pointer'
              }}
            >
              Créer un compte
            </button>
          </div>
        )}

        {/* MESSAGES DE SUCCES / ERREUR */}
        {error && (
          <div style={{background: '#FCEBEB', border: '1px solid #F5B5B5', borderRadius: '8px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#A32D2D', wordBreak: 'break-word'}}>
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{background: '#E1F5EE', border: '1px solid #A7F3D0', borderRadius: '8px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#0F6E56', wordBreak: 'break-word'}}>
            {successMessage}
          </div>
        )}

        {/* 1. FORMULAIRE DE CONNEXION */}
        {viewState === 'login' && (
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                style={{width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'}}
                onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
              />
            </div>

            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>
                Mot de passe
              </label>
              <div style={{position: 'relative'}}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{width: '100%', padding: '12px', paddingRight: '40px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'}}
                  onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                  onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>

              {/* Mot de passe oublié */}
              <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '10px'}}>
                <button
                  type="button"
                  onClick={() => { setViewState('forgot'); setError(''); setSuccessMessage(''); }}
                  style={{background: 'none', border: 'none', color: '#1D9E75', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: '0'}}
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{width: '100%', padding: '14px', background: loading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s'}}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#0F6E56')}
              onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        )}

        {/* 1b. FORMULAIRE D'INSCRIPTION */}
        {viewState === 'register' && (
          <form onSubmit={handleRegisterSubmit}>
            
            {/* Toggle Créateur d'entreprise sans SIREN */}
            <div style={{ background: '#F3F4F6', padding: '12px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="creator-mode-checkbox"
                checked={isCreatorMode}
                onChange={(e) => setIsCreatorMode(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#1D9E75', cursor: 'pointer' }}
              />
              <label htmlFor="creator-mode-checkbox" style={{ fontSize: '13px', fontWeight: '500', color: '#1F2937', cursor: 'pointer', margin: 0 }}>
                💡 En cours de création (sans SIREN pour l'instant)
              </label>
            </div>

            <div style={{marginBottom: '14px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '6px'}}>
                {isCreatorMode ? "Nom du projet d'entreprise" : "Nom de l'entreprise"} <span style={{ color: '#D85A30' }}>*</span>
              </label>
              <input
                type="text"
                value={registerNom}
                onChange={(e) => setRegisterNom(e.target.value)}
                placeholder={isCreatorMode ? "Ex: Projet Consulting IT" : "Ex: Anges Mobilité"}
                required
                style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none'}}
              />
            </div>

            {!isCreatorMode && (
              <div style={{marginBottom: '14px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '6px'}}>
                  SIREN (9 chiffres) <span style={{ color: '#D85A30' }}>*</span>
                </label>
                <input
                  type="text"
                  value={registerSiren}
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, '');
                    setRegisterSiren(onlyNumbers.slice(0, 9));
                  }}
                  placeholder="Ex: 123456789"
                  required={!isCreatorMode}
                  maxLength={9}
                  style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none'}}
                />
              </div>
            )}

            <div style={{marginBottom: '14px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '6px'}}>
                Email de connexion <span style={{ color: '#D85A30' }}>*</span>
              </label>
              <input
                type="email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                placeholder="contact@entreprise.fr"
                required
                style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none'}}
              />
            </div>

            <div style={{marginBottom: '14px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '6px'}}>
                Mot de passe <span style={{ color: '#D85A30' }}>*</span>
              </label>
              <div style={{position: 'relative'}}>
                <input
                  type={showRegisterPassword ? 'text' : 'password'}
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  placeholder="Min 8 car., 1 Maj, 1 Min, 1 Chif."
                  required
                  style={{width: '100%', padding: '10px 12px', paddingRight: '40px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none'}}
                />
                <button
                  type="button"
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0'}}
                >
                  {showRegisterPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div style={{marginBottom: '14px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '6px'}}>
                Confirmer le mot de passe <span style={{ color: '#D85A30' }}>*</span>
              </label>
              <input
                type="password"
                value={registerConfirmPassword}
                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                placeholder="Confirmez votre mot de passe"
                required
                style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none'}}
              />
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px'}}>
              <div>
                <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#2D2D2D', marginBottom: '4px'}}>
                  Régime TVA
                </label>
                <select
                  value={registerRegime}
                  onChange={(e) => setRegisterRegime(e.target.value)}
                  style={{width: '100%', padding: '8px', border: '1px solid #E8E7E3', borderRadius: '6px', fontSize: '13px', background: 'white'}}
                >
                  <option value="reel_normal">Réel Normal</option>
                  <option value="reel_simplifie">Réel Simplifié</option>
                  <option value="franchise">Franchise de Base</option>
                </select>
              </div>
              <div>
                <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#2D2D2D', marginBottom: '4px'}}>
                  Fréquence TVA
                </label>
                <select
                  value={registerFrequence}
                  onChange={(e) => setRegisterFrequence(e.target.value)}
                  style={{width: '100%', padding: '8px', border: '1px solid #E8E7E3', borderRadius: '6px', fontSize: '13px', background: 'white'}}
                >
                  <option value="mensuelle">Mensuelle</option>
                  <option value="trimestrielle">Trimestrielle</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{width: '100%', padding: '14px', background: loading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s'}}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#0F6E56')}
              onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
            >
              {loading ? 'Création du compte...' : 'Créer mon compte'}
            </button>
          </form>
        )}

        {/* 2. FORMULAIRE MOT DE PASSE OUBLIÉ */}
        {viewState === 'forgot' && (
          <form onSubmit={handleSendResetLink}>
            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>
                Adresse E-mail
              </label>
              <input
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                style={{width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'}}
                onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{width: '100%', padding: '14px', background: loading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s', marginBottom: '16px'}}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#0F6E56')}
              onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
            >
              {loading ? 'Envoi du lien...' : 'Envoyer le lien de récupération'}
            </button>

            <button
              type="button"
              onClick={() => { setViewState('login'); setError(''); setSuccessMessage(''); }}
              style={{width: '100%', padding: '12px', background: 'white', color: '#73726C', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s'}}
              onMouseEnter={(e) => e.target.style.background = '#FAF9F5'}
              onMouseLeave={(e) => e.target.style.background = 'white'}
            >
              Retour à la connexion
            </button>
          </form>
        )}

        {/* 3. FORMULAIRE DE MISE À JOUR (RESET) */}
        {viewState === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'}}
                onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
              />
            </div>

            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'}}
                onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{width: '100%', padding: '14px', background: loading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s', marginBottom: '16px'}}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#0F6E56')}
              onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer le mot de passe'}
            </button>

            <button
              type="button"
              onClick={() => {
                if (onCancelRecovery) {
                  onCancelRecovery();
                } else {
                  setViewState('login');
                  setError('');
                  setSuccessMessage('');
                }
              }}
              style={{width: '100%', padding: '12px', background: 'white', color: '#73726C', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s'}}
              onMouseEnter={(e) => e.target.style.background = '#FAF9F5'}
              onMouseLeave={(e) => e.target.style.background = 'white'}
            >
              Annuler
            </button>
          </form>
        )}

        {/* Lien retour accueil */}
        {(viewState === 'login' || viewState === 'register') && (
          <div style={{marginTop: '24px', textAlign: 'center'}}>
            <button
              type="button"
              onClick={() => window.location.href = '/'}
              style={{background: 'none', border: 'none', color: '#73726C', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline'}}
            >
              ← Retour à l'accueil
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ClientLogin;
